// shadi-arts-portfolio Firebase\client\src\components\Admin\OrderManager.jsx  


import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { useLanguage } from '../../context/LanguageContext';

const OrderManager = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'orders'));
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrders(items.sort((a, b) => new Date(b.timestamps.created) - new Date(a.timestamps.created)));
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        'status.order': newStatus,
        'timestamps.updated': new Date().toISOString()
      });
      fetchOrders();
      alert(t(`Order status updated to ${newStatus}`, `تم تحديث حالة الطلب إلى ${newStatus}`));
    } catch (error) {
      console.error('Error updating order:', error);
      alert(t('Error updating order', 'حدث خطأ أثناء تحديث الطلب'));
    }
  };

  const deleteOrder = async (orderId) => {
    if (window.confirm(t('Are you sure you want to delete this order?', 'هل أنت متأكد من حذف هذا الطلب؟'))) {
      try {
        await deleteDoc(doc(db, 'orders', orderId));
        fetchOrders();
        alert(t('Order deleted successfully', 'تم حذف الطلب بنجاح'));
      } catch (error) {
        console.error('Error deleting order:', error);
        alert(t('Error deleting order', 'حدث خطأ أثناء حذف الطلب'));
      }
    }
  };

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(order => order.status?.order === filter);

  if (loading) return <div className="loading">{t("Loading orders...", "جاري تحميل الطلبات...")}</div>;

  // Status translation mapping
  const getTranslatedStatus = (status) => {
    const translations = {
      pending: t("pending", "معلق"),
      confirmed: t("confirmed", "مؤكد"),
      'in-progress': t("in-progress", "قيد التنفيذ"),
      completed: t("completed", "مكتمل"),
      cancelled: t("cancelled", "ملغي")
    };
    return translations[status] || status;
  };

  return (
    <div className="order-manager">
      <div className="order-filters">
        <button onClick={() => setFilter('all')} className={filter === 'all' ? 'active' : ''}>
          {t("All Orders", "جميع الطلبات")}
        </button>
        <button onClick={() => setFilter('pending')} className={filter === 'pending' ? 'active' : ''}>
          {t("Pending", "معلق")}
        </button>
        <button onClick={() => setFilter('confirmed')} className={filter === 'confirmed' ? 'active' : ''}>
          {t("Confirmed", "مؤكد")}
        </button>
        <button onClick={() => setFilter('completed')} className={filter === 'completed' ? 'active' : ''}>
          {t("Completed", "مكتمل")}
        </button>
      </div>
      
      <div className="orders-list">
        {filteredOrders.length === 0 ? (
          <p className="no-orders">{t("No orders found", "لا توجد طلبات")}</p>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.id} className="order-card">
              <div className="order-header">
                <span className="order-id">{t("Order", "طلب")} #{order.id.slice(0, 8)}</span>
                <span className={`order-status status-${order.status?.order || 'pending'}`}>
                  {getTranslatedStatus(order.status?.order || 'pending')}
                </span>
                <span className="order-date">
                  {new Date(order.timestamps.created).toLocaleDateString()}
                </span>
              </div>
              
              <div className="order-body">
                <div className="order-customer">
                  <h4>{t("Customer Details", "بيانات العميل")}</h4>
                  <p><strong>{t("Name", "الاسم")}:</strong> {order.customer.name}</p>
                  <p><strong>{t("Email", "البريد الإلكتروني")}:</strong> {order.customer.email}</p>
                  {order.customer.phone && <p><strong>{t("Phone", "الهاتف")}:</strong> {order.customer.phone}</p>}
                  <p><strong>{t("Country", "الدولة")}:</strong> {order.customer.country}</p>
                </div>
                
                <div className="order-details">
                  <h4>{t("Order Details", "تفاصيل الطلب")}</h4>
                  <p><strong>{t("Portrait Type", "نوع البورتريه")}:</strong> {order.orderDetails.portraitType}</p>
                  <p><strong>{t("Size", "المقاس")}:</strong> {order.orderDetails.paperSize}</p>
                  <p><strong>{t("Subjects", "عدد الأشخاص")}:</strong> {order.orderDetails.subjectCount}</p>
                  <p><strong>{t("Frame", "إطار")}:</strong> {order.orderDetails.includeFrame ? t("Yes", "نعم") : t("No", "لا")}</p>
                  <p><strong>{t("Total Price", "السعر الإجمالي")}:</strong> EGP {order.pricing.estimated}</p>
                </div>
              </div>
              
              {order.specialInstructions && (
                <div className="order-instructions">
                  <strong>{t("Special Instructions", "تعليمات خاصة")}:</strong>
                  <p>{order.specialInstructions}</p>
                </div>
              )}
              
              <div className="order-actions">
                <select 
                  value={order.status?.order || 'pending'}
                  onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                  className="status-select"
                >
                  <option value="pending">{t("Pending", "معلق")}</option>
                  <option value="confirmed">{t("Confirmed", "مؤكد")}</option>
                  <option value="in-progress">{t("In Progress", "قيد التنفيذ")}</option>
                  <option value="completed">{t("Completed", "مكتمل")}</option>
                  <option value="cancelled">{t("Cancelled", "ملغي")}</option>
                </select>
                <button onClick={() => deleteOrder(order.id)} className="btn-delete-order">
                  {t("Delete Order", "حذف الطلب")}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OrderManager;  